import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  Save,
  Building,
  Sparkles,
  Search,
  AlertCircle,
  FileText,
  Loader2,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Settings State matching site_settings key/values
  const [generalSettings, setGeneralSettings] = useState({
    business_name: 'VJ Dsign Studio',
    tagline: 'Transforming Spaces into Living Art',
    email: 'vjdsignstudio@gmail.com',
    phone: '+91 87545 45419',
    phone_raw: '918754545419',
    address: 'Tiruppur, Tamil Nadu, India',
    whatsapp: '+91 87545 45419',
    instagram_url: 'https://www.instagram.com/vjdsignstudio/',
    facebook_url: 'https://www.facebook.com/share/16xZtiEpY5/',
  });

  const [heroSettings, setHeroSettings] = useState({
    badge: 'Architecture • Interior Design Studio',
    title_line1: 'Transforming',
    title_highlight: 'Spaces Into Art',
    description:
      'Creating elegant interiors and innovative architectural solutions that blend beauty, comfort, and functionality for modern living.',
    cta_primary_text: 'Explore Projects',
    cta_primary_link: '#projects',
    cta_secondary_text: 'View Packages',
    cta_secondary_link: '#packages',
  });

  const [aboutSettings, setAboutSettings] = useState({
    tagline: 'Designing Spaces With Purpose',
    description:
      "VJ Dsign Studio specializes in Interior Design, Residential Projects, and Renovation Works. We collaborate with direct clients, architects, and engineers to deliver functional, elegant, and customized spaces that reflect every client's vision.",
    experience_year: 'Since 2017',
    experience_text: 'Creating Beautiful Spaces With Excellence',
  });

  const [seoSettings, setSeoSettings] = useState({
    meta_title: 'VJ Dsign Studio - Premium Interior Design & Architecture',
    meta_description:
      'Discover modern residential and commercial interior architecture, modular kitchens, luxury wardrobes, and full renovation solutions by VJ Dsign Studio in Tiruppur.',
    keywords:
      'VJ Dsign Studio, Interior Design, Tiruppur, Modular Kitchen, Living Room Design, Renovation, Commercial Interiors',
  });

  const toast = useToast();

  const loadSettings = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.siteSettings.getAll();

      if (data) {
        if (data.general) {
          setGeneralSettings((prev) => ({ ...prev, ...data.general }));
        }
        if (data.hero) {
          setHeroSettings((prev) => ({ ...prev, ...data.hero }));
        }
        if (data.about) {
          setAboutSettings((prev) => ({ ...prev, ...data.about }));
        }
        if (data.seo) {
          setSeoSettings((prev) => ({ ...prev, ...data.seo }));
        }
      }
    } catch (err) {
      console.error('Error loading site settings:', err);
      setError(err.message || 'Failed to fetch site settings from Supabase.');
      toast.error('Failed to load settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Upsert all 4 settings groups in Supabase
      await Promise.all([
        queries.siteSettings.upsert('general', generalSettings),
        queries.siteSettings.upsert('hero', heroSettings),
        queries.siteSettings.upsert('about', aboutSettings),
        queries.siteSettings.upsert('seo', seoSettings),
      ]);
      toast.success('Site settings and content successfully saved to Supabase');
    } catch (err) {
      console.error('Save settings error:', err);
      toast.error('Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'Studio Info & Brand', icon: <Building size={16} /> },
    { id: 'hero', label: 'Hero Home Section', icon: <Sparkles size={16} /> },
    { id: 'about', label: 'About Us & Bio', icon: <FileText size={16} /> },
    { id: 'seo', label: 'SEO & Search Engine', icon: <Search size={16} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <Settings size={24} className="text-[#9BB7A3]" />
            Site Content & Brand Settings
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Configure live studio metadata, hero copy, bio text, social channels, and search engine tags.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 disabled:opacity-50 shrink-0"
        >
          <Save size={16} />
          <span>{saving ? 'Saving...' : 'Save All Settings'}</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-900/60 p-2 rounded-2xl border border-slate-800">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-[#9BB7A3] text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main Settings Form Container */}
      {loading ? (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-16 flex flex-col items-center justify-center text-center space-y-4 shadow-xl">
          <Loader2 size={36} className="animate-spin text-[#9BB7A3]" />
          <div className="text-sm font-semibold text-slate-300">Loading live site settings from Supabase...</div>
          <div className="text-xs text-slate-500">Fetching brand details, hero copy, and SEO configurations</div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl">
          {/* Tab 1: General Info */}
          {activeTab === 'general' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <Building size={18} className="text-[#9BB7A3]" />
              Studio Identity & Contact Channels
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Studio / Business Name
                </label>
                <input
                  type="text"
                  value={generalSettings.business_name}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, business_name: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Tagline / Brand Slogan
                </label>
                <input
                  type="text"
                  value={generalSettings.tagline}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, tagline: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Studio Phone
                </label>
                <input
                  type="text"
                  value={generalSettings.phone}
                  onChange={(e) => {
                    const phone = e.target.value;
                    const phone_raw = phone.replace(/[^0-9]/g, '');
                    setGeneralSettings({ ...generalSettings, phone, phone_raw });
                  }}
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Studio Email
                </label>
                <input
                  type="email"
                  value={generalSettings.email}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, email: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  WhatsApp Contact
                </label>
                <input
                  type="text"
                  value={generalSettings.whatsapp}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, whatsapp: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Physical Studio Address
              </label>
              <input
                type="text"
                value={generalSettings.address}
                onChange={(e) =>
                  setGeneralSettings({ ...generalSettings, address: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Instagram Profile URL
                </label>
                <input
                  type="url"
                  value={generalSettings.instagram_url}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, instagram_url: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Facebook Profile URL
                </label>
                <input
                  type="url"
                  value={generalSettings.facebook_url}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, facebook_url: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Hero Section */}
        {activeTab === 'hero' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <Sparkles size={18} className="text-[#9BB7A3]" />
              Hero Section Headline & Call To Action
            </h2>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Top Badge Text
              </label>
              <input
                type="text"
                value={heroSettings.badge}
                onChange={(e) => setHeroSettings({ ...heroSettings, badge: e.target.value })}
                placeholder="Architecture • Interior Design Studio"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Headline Line 1
                </label>
                <input
                  type="text"
                  value={heroSettings.title_line1}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, title_line1: e.target.value })
                  }
                  placeholder="Transforming"
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Highlighted Headline (Sage Green)
                </label>
                <input
                  type="text"
                  value={heroSettings.title_highlight}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, title_highlight: e.target.value })
                  }
                  placeholder="Spaces Into Art"
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Hero Subtitle Description
              </label>
              <textarea
                rows={3}
                value={heroSettings.description}
                onChange={(e) =>
                  setHeroSettings({ ...heroSettings, description: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Primary Button Text
                </label>
                <input
                  type="text"
                  value={heroSettings.cta_primary_text}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, cta_primary_text: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Primary Button Link
                </label>
                <input
                  type="text"
                  value={heroSettings.cta_primary_link}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, cta_primary_link: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Secondary Button Text
                </label>
                <input
                  type="text"
                  value={heroSettings.cta_secondary_text}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, cta_secondary_text: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Secondary Button Link
                </label>
                <input
                  type="text"
                  value={heroSettings.cta_secondary_link}
                  onChange={(e) =>
                    setHeroSettings({ ...heroSettings, cta_secondary_link: e.target.value })
                  }
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: About Us */}
        {activeTab === 'about' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <FileText size={18} className="text-[#9BB7A3]" />
              About Us Story & Experience Milestones
            </h2>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                About Us Tagline
              </label>
              <input
                type="text"
                value={aboutSettings.tagline}
                onChange={(e) => setAboutSettings({ ...aboutSettings, tagline: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Studio Narrative Bio
              </label>
              <textarea
                rows={4}
                value={aboutSettings.description}
                onChange={(e) =>
                  setAboutSettings({ ...aboutSettings, description: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Experience Year Badge
                </label>
                <input
                  type="text"
                  value={aboutSettings.experience_year}
                  onChange={(e) =>
                    setAboutSettings({ ...aboutSettings, experience_year: e.target.value })
                  }
                  placeholder="Since 2017"
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Experience Subtitle
                </label>
                <input
                  type="text"
                  value={aboutSettings.experience_text}
                  onChange={(e) =>
                    setAboutSettings({ ...aboutSettings, experience_text: e.target.value })
                  }
                  placeholder="Creating Beautiful Spaces With Excellence"
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: SEO */}
        {activeTab === 'seo' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white flex items-center gap-2 border-b border-slate-800 pb-3">
              <Search size={18} className="text-[#9BB7A3]" />
              SEO & Google Search Engine Metadata
            </h2>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Meta Title (Appears in Browser Tab & Google Snippets)
              </label>
              <input
                type="text"
                value={seoSettings.meta_title}
                onChange={(e) =>
                  setSeoSettings({ ...seoSettings, meta_title: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Meta Description
              </label>
              <textarea
                rows={3}
                value={seoSettings.meta_description}
                onChange={(e) =>
                  setSeoSettings({ ...seoSettings, meta_description: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Keywords (Comma-separated)
              </label>
              <input
                type="text"
                value={seoSettings.keywords}
                onChange={(e) =>
                  setSeoSettings({ ...seoSettings, keywords: e.target.value })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-slate-800">
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={15} />
            <span>{saving ? 'Saving All Settings...' : 'Save Settings'}</span>
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
